import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import iconHubsales from '../assets/icon-hubsales.png';
import '../login/styles.css';
import { criarConta, esqueciSenha, definirSenha, redefinirSenha, mensagemDe } from '../services/motor';

/**
 * As quatro telas da CONTA do cliente (nível 1, 04/09/2026), todas no mesmo
 * molde da tela de entrada:
 *   /criar-conta   → pede o e-mail; o backoffice manda o link para criar a senha
 *   /esqueci       → pede o e-mail; o backoffice manda o link de 15 min
 *   /definir-senha → o link do e-mail de criação (token de primeiro acesso)
 *   /reset         → o link do e-mail de recuperação (id + token)
 * A senha nunca é definida sem o link: é a prova de que o e-mail é da pessoa.
 */
function Moldura({ titulo, children }) {
    useEffect(() => { document.title = "HubsAI | " + titulo; }, [titulo]);
    return (
        <div className="container">
            <div className="container-login">
                <div className="wrap-login">
                    <div className="login-form">
                        <span className="login-form-title"><img src={iconHubsales} alt="HubsAI" /></span>
                        <span className="login-form-title">{titulo}</span>
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Campo({ tipo, valor, setValor, rotulo, autoComplete }) {
    return (
        <div className="wrap-input">
            <input className={valor !== "" ? 'has-val input' : 'input'} type={tipo} value={valor} required
                autoComplete={autoComplete} onChange={(e) => setValor(e.target.value)} />
            <span className="focus-input" data-placeholder={rotulo}></span>
        </div>
    );
}

function Aviso({ texto }) {
    return texto ? <div className="container-login-form-btn"><span style={{ color: '#7c3aed', textAlign: 'center' }}>{texto}</span></div> : null;
}

const SENHA_MINIMA = 8;
function senhaOk(senha, confirma) {
    if (senha.length < SENHA_MINIMA) return `A senha precisa ter pelo menos ${SENHA_MINIMA} caracteres.`;
    if (senha !== confirma) return "As duas senhas não são iguais.";
    return null;
}

export function CriarConta() {
    const [email, setEmail] = useState("");
    const [nome, setNome] = useState("");
    const [aviso, setAviso] = useState("");
    const [feito, setFeito] = useState(false);
    const [enviando, setEnviando] = useState(false);
    async function submeter(e) {
        e.preventDefault();
        setEnviando(true);
        try {
            const r = await criarConta(String(email).trim().toLowerCase(), nome);
            setAviso(r.data.message);
            setFeito(true);
        } catch (err) {
            setAviso(mensagemDe(err, "Não consegui criar a conta agora. Tente de novo."));
        } finally { setEnviando(false); }
    }
    return (
        <Moldura titulo="Criar conta">
            {feito ? (
                <>
                    <Aviso texto={aviso} />
                    <div className="container-login-form-btn"><span>Abra o e-mail, clique no link e crie a sua senha. Depois volte e entre.</span></div>
                    <div className="container-login-form-btn"><Link className='ticket' to='/authenticate'>Voltar para a entrada</Link></div>
                </>
            ) : (
                <form onSubmit={submeter}>
                    <Campo tipo="text" valor={nome} setValor={setNome} rotulo="Seu nome (opcional)" autoComplete="name" />
                    <Campo tipo="email" valor={email} setValor={setEmail} rotulo="E-mail da sua loja" autoComplete="username" />
                    <div className="container-login-form-btn">
                        <button disabled={enviando} className={email !== "" ? "login-form-btn2" : "login-form-btn"}>{enviando ? "Enviando…" : "Receber o link por e-mail"}</button>
                    </div>
                    <Aviso texto={aviso} />
                    <div className="container-login-form-btn"><span>Você recebe um e-mail com o link para criar a senha. Ele vale por 7 dias.</span></div>
                    <div className="container-login-form-btn"><Link className='ticket' to='/authenticate'>Já tenho conta</Link></div>
                </form>
            )}
        </Moldura>
    );
}

export function Esqueci() {
    const [email, setEmail] = useState("");
    const [aviso, setAviso] = useState("");
    const [enviando, setEnviando] = useState(false);
    // (05/09) "Esqueci a senha" também é o caminho de quem NUNCA teve senha
    // (e-mail já na base, ou que já abriu chamado): o `cadastrar` do backoffice
    // decide — tem senha → link de recuperação (15 min); não tem → link para
    // criar a senha (7 dias); só tem histórico de chamado → cria a conta e manda
    // o link. Uma tela só, para o cliente não precisar saber em qual caso está.
    async function submeter(e) {
        e.preventDefault();
        setEnviando(true);
        try {
            const r = await criarConta(String(email).trim().toLowerCase(), "");
            setAviso(r.data.message || "Se este e-mail puder abrir chamados, você recebe em instantes um e-mail com o próximo passo.");
        } catch (err) {
            setAviso(mensagemDe(err, "Não consegui enviar o e-mail agora. Tente de novo."));
        } finally { setEnviando(false); }
    }
    return (
        <Moldura titulo="Esqueci a senha">
            <form onSubmit={submeter}>
                <Campo tipo="email" valor={email} setValor={setEmail} rotulo="E-mail" autoComplete="username" />
                <div className="container-login-form-btn">
                    <button disabled={enviando} className={email !== "" ? "login-form-btn2" : "login-form-btn"}>{enviando ? "Enviando…" : "Enviar link por e-mail"}</button>
                </div>
                <Aviso texto={aviso} />
                <div className="container-login-form-btn"><span>Serve também para criar a sua primeira senha. Abra o e-mail e clique no botão.</span></div>
                <div className="container-login-form-btn"><Link className='ticket' to='/authenticate'>Voltar para a entrada</Link></div>
            </form>
        </Moldura>
    );
}

export function DefinirSenha() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";
    const [senha, setSenha] = useState("");
    const [confirma, setConfirma] = useState("");
    const [aviso, setAviso] = useState(token ? "" : "Este link está incompleto. Abra o link exatamente como veio no e-mail.");
    const [enviando, setEnviando] = useState(false);
    async function submeter(e) {
        e.preventDefault();
        const erro = senhaOk(senha, confirma);
        if (erro) { setAviso(erro); return; }
        setEnviando(true);
        try {
            await definirSenha(token, senha);
            navigate("/authenticate?ok=" + encodeURIComponent("Senha criada. Agora é só entrar."));
        } catch (err) {
            const codigo = err && err.response && err.response.data && err.response.data.error;
            setAviso(codigo === 'ALREADY_HAVE_PASSWORD'
                ? "Esta conta já tem senha. Use \"Esqueci a senha\" para trocar."
                : mensagemDe(err, "Não consegui definir a senha. O link pode ter vencido — peça outro em \"Criar conta\"."));
        } finally { setEnviando(false); }
    }
    return (
        <Moldura titulo="Crie a sua senha">
            <form onSubmit={submeter}>
                <Campo tipo="password" valor={senha} setValor={setSenha} rotulo="Nova senha (mín. 8 caracteres)" autoComplete="new-password" />
                <Campo tipo="password" valor={confirma} setValor={setConfirma} rotulo="Repita a senha" autoComplete="new-password" />
                <div className="container-login-form-btn">
                    <button disabled={enviando || !token} className={senha !== "" && confirma !== "" ? "login-form-btn2" : "login-form-btn"}>{enviando ? "Salvando…" : "Salvar senha"}</button>
                </div>
                <Aviso texto={aviso} />
                <div className="container-login-form-btn"><Link className='ticket' to='/criar-conta'>Pedir outro link</Link></div>
            </form>
        </Moldura>
    );
}

export function Redefinir() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const id = params.get("id") || "";
    const token = params.get("token") || "";
    const [senha, setSenha] = useState("");
    const [confirma, setConfirma] = useState("");
    const [aviso, setAviso] = useState(id && token ? "" : "Este link está incompleto. Abra o link exatamente como veio no e-mail.");
    const [enviando, setEnviando] = useState(false);
    async function submeter(e) {
        e.preventDefault();
        const erro = senhaOk(senha, confirma);
        if (erro) { setAviso(erro); return; }
        setEnviando(true);
        try {
            await redefinirSenha(Number(id), token, senha);
            navigate("/authenticate?ok=" + encodeURIComponent("Senha redefinida. Agora é só entrar."));
        } catch (err) {
            setAviso(mensagemDe(err, "Não consegui redefinir. O link pode ter vencido — peça outro em \"Esqueci a senha\"."));
        } finally { setEnviando(false); }
    }
    return (
        <Moldura titulo="Redefinir a senha">
            <form onSubmit={submeter}>
                <Campo tipo="password" valor={senha} setValor={setSenha} rotulo="Nova senha (mín. 8 caracteres)" autoComplete="new-password" />
                <Campo tipo="password" valor={confirma} setValor={setConfirma} rotulo="Repita a senha" autoComplete="new-password" />
                <div className="container-login-form-btn">
                    <button disabled={enviando || !id || !token} className={senha !== "" && confirma !== "" ? "login-form-btn2" : "login-form-btn"}>{enviando ? "Salvando…" : "Salvar senha"}</button>
                </div>
                <Aviso texto={aviso} />
                <div className="container-login-form-btn"><Link className='ticket' to='/esqueci'>Pedir outro link</Link></div>
            </form>
        </Moldura>
    );
}
